// src/contexts/SuperAdminContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { parseJwt } from '../utils/auth';

export const SuperAdminContext = createContext({
  isSuperAdmin: false,
  organizations: [],
  loading: false,
  error: null,
  fetchOrganizations: () => {},
});

export const SuperAdminProvider = ({ children }) => {
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is super admin based on JWT token
    const checkSuperAdminStatus = () => {
      const token = localStorage.getItem('authToken');
      if (token) {
        const decodedToken = parseJwt(token);
        if (decodedToken && decodedToken.roles) {
          setIsSuperAdmin(decodedToken.roles.includes('ROLE_SUPER_ADMIN'));
        }
      }
    };

    checkSuperAdminStatus();
    
    // If super admin, fetch organizations
    if (isSuperAdmin) {
      fetchOrganizations();
    }
  }, [isSuperAdmin]);

  const fetchOrganizations = async () => {
    if (!isSuperAdmin) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('/api/organizations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch organizations');
      }
      
      const data = await response.json();
      setOrganizations(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching organizations:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SuperAdminContext.Provider 
      value={{ 
        isSuperAdmin, 
        organizations,
        loading,
        error,
        fetchOrganizations
      }}
    >
      {children}
    </SuperAdminContext.Provider>
  );
};

export const useSuperAdmin = () => useContext(SuperAdminContext);